import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { ScoreGauge } from '../charts/Charts';
import { MapView } from '../map/MapView';
import { AlertCard } from '../alerts/AlertCard';
import { VehicleCameraViewer } from '../vehicles/VehicleCameraViewer';
import { useVehicles } from '../../hooks/useVehicles';
import { useAlertsContext } from '../../context/AlertsContext';
import { useAuth } from '../../context/AuthContext';
import { RISK_LABELS, RISK_COLORS } from '../../utils/constants';
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, Star,
  MapPin, Bell, Video, Phone
} from 'lucide-react';
import type { Driver } from '../../types/driver';

interface DriverProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  driver: Driver | null;
}

type ActiveTab = 'map' | 'alerts' | 'camera';

export function DriverProfileModal({ isOpen, onClose, driver }: DriverProfileModalProps) {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [activeTab, setActiveTab] = useState<ActiveTab>('map');

  const { vehicles } = useVehicles();
  const { alerts } = useAlertsContext();
  // El Operador no tiene permiso para ver video en vivo (PRODUCT.md §4.8) —
  // en la práctica no llega aquí (Conductores no está en su nav), pero el
  // gate se aplica igual que en VehicleDetailPanel/Modal, por si acaso.
  const { user } = useAuth();
  const canViewVideo = user?.role !== 'operator';

  if (!driver) return null;

  const color = driver.score ? RISK_COLORS[driver.score.level] : 'var(--text-muted)';
  const TrendIcon = driver.score?.trend === 'up'
    ? TrendingUp : driver.score?.trend === 'down' ? TrendingDown : Minus;

  const assignedVehicle = vehicles.find(v => v.id === driver.assignedVehicleId || v.driverId === driver.id);
  const driverAlerts = alerts.filter(a => a.driverId === driver.id);

  const tabConfig: { id: ActiveTab; label: string; icon: React.ReactNode }[] = [
    { id: 'map', label: 'Mapa en Vivo', icon: <MapPin size={15} /> },
    ...(canViewVideo ? [{ id: 'camera' as const, label: 'Cámara en Vivo', icon: <Video size={15} /> }] : []),
    { id: 'alerts', label: `Alertas (${driverAlerts.length})`, icon: <Bell size={15} /> },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { setIsFullScreen(false); onClose(); }}
      title={`Perfil: ${driver.name}`}
      size="lg"
      fullScreen={isFullScreen}
      onToggleFullScreen={() => setIsFullScreen(!isFullScreen)}
    >
      <div className="driver-profile-modal">
        {/* Header / Stats */}
        <div className="driver-profile-modal__header" style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          <div className="operator-page__hero-left" style={{ flex: '1', minWidth: '250px', background: 'var(--bg-raised)', padding: '1.5rem', borderRadius: 'var(--radius-lg)' }}>
            <div className="operator-page__avatar">{driver.name.charAt(0)}</div>
            <div>
              <h2 className="operator-page__name">{driver.name}</h2>
              <p className="operator-page__vehicle">
                {driver.assignedVehiclePlate ? `Vehículo: ${driver.assignedVehiclePlate}` : 'Sin vehículo asignado'}
              </p>
              <p className="operator-page__unit">{driver.subUnitName}</p>
              {/* issue #121: redirección a tel: — detalle laxo, sin backend
                  nuevo. No confundir con EmergencyCallModal (VoIP simulado):
                  esto es simplemente "abrir el marcador del teléfono", que
                  es lo que el acta pidió. */}
              {driver.phone && (
                <a
                  href={`tel:${driver.phone}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', marginTop: '0.375rem', fontSize: '13px', fontWeight: 600, color: 'var(--accent)', textDecoration: 'none' }}
                >
                  <Phone size={14} /> {driver.phone}
                </a>
              )}
            </div>
          </div>

          <div className="operator-page__score-block" style={{ flex: '1', minWidth: '250px', background: 'var(--bg-raised)', padding: '1.5rem', borderRadius: 'var(--radius-lg)' }}>
            {driver.score ? (
              <>
                <ScoreGauge score={driver.score.value} size={100} />
                <div className="operator-page__score-meta">
                  <span className="operator-page__risk-badge" style={{ color, background: `${color}18`, borderColor: `${color}44` }}>
                    {RISK_LABELS[driver.score.level]}
                  </span>
                  <div className="operator-page__trend" style={{ color }}>
                    <TrendIcon size={16} />
                  </div>
                </div>
              </>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)' }}>
                <span style={{ fontSize: '13px' }}>Score pendiente de cálculo</span>
              </div>
            )}
          </div>
        </div>

        {/* FE-04: críticos/medios/leves salen de las alertas reales del conductor
            (mismo origen que driverAlerts). FE-19 retiró el cuarto tile: ver abajo. */}
        <div className="operator-page__stats" style={{ marginBottom: '1.5rem' }}>
          <div className="operator-page__stat">
            <AlertTriangle size={18} style={{ color: '#EF4444' }} />
            <span className="operator-page__stat-value">{driverAlerts.filter(a => a.severity === 'critical').length}</span>
            <span className="operator-page__stat-label">Críticos</span>
          </div>
          <div className="operator-page__stat">
            <AlertTriangle size={18} style={{ color: '#F59E0B' }} />
            <span className="operator-page__stat-value">{driverAlerts.filter(a => a.severity === 'medium').length}</span>
            <span className="operator-page__stat-label">Medios</span>
          </div>
          <div className="operator-page__stat">
            <Star size={18} style={{ color: '#10B981' }} />
            <span className="operator-page__stat-value">{driverAlerts.filter(a => a.severity === 'low').length}</span>
            <span className="operator-page__stat-label">Leves</span>
          </div>
          {/* FE-19: el tile «Recorridos» se RETIRÓ. Un conductor no tiene
              odómetro: rota entre vehículos (driver_vehicle_assignment con
              vigencia), así que el del vehículo que maneja hoy incluye todo lo
              que ese equipo recorrió con otros conductores y sin ninguno.
              Atribuírselo sería inventar un dato que ni la base ni el contrato
              respaldan. El odómetro se ve donde pertenece: en el vehículo. */}
        </div>

        {/* Tab Selection */}
        <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem', flexWrap: 'wrap' }}>
          {tabConfig.map(tab => (
            <button
              key={tab.id}
              id={`driver-tab-${tab.id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.5rem 0.875rem', borderRadius: 'var(--radius-md)', border: 'none',
                background: activeTab === tab.id ? 'var(--accent)' : 'var(--bg-raised)',
                color: activeTab === tab.id ? '#FFF' : 'var(--text-secondary)',
                fontWeight: 600, cursor: 'pointer', fontSize: '13px',
                transition: 'all var(--trans-fast)',
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content: Map */}
        {activeTab === 'map' && (
          <div className="driver-profile-modal__map" style={{
            height: isFullScreen ? 'calc(100vh - 420px)' : '350px',
            minHeight: '300px', borderRadius: '16px', overflow: 'hidden',
            border: '1px solid var(--border)', display: 'flex', flexDirection: 'column'
          }}>
            {assignedVehicle ? (
              <MapView vehicles={[assignedVehicle]} selectedVehicle={assignedVehicle} />
            ) : (
              <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-surface)', color: 'var(--text-muted)' }}>
                El conductor no tiene un vehículo en ruta activo.
              </div>
            )}
          </div>
        )}

        {/* Tab Content: Camera */}
        {activeTab === 'camera' && canViewVideo && (
          assignedVehicle ? (
            <VehicleCameraViewer vehicleId={assignedVehicle.id} />
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-raised)', borderRadius: 'var(--radius-lg)' }}>
              El conductor no tiene un vehículo en ruta activo.
            </div>
          )
        )}

        {/* Tab Content: Alerts */}
        {activeTab === 'alerts' && (
          <div className="driver-profile-modal__alerts" style={{
            display: 'flex', flexDirection: 'column', gap: '0.75rem',
            maxHeight: isFullScreen ? 'calc(100vh - 420px)' : '350px',
            overflowY: 'auto', paddingRight: '0.25rem'
          }}>
            {driverAlerts.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', background: 'var(--bg-raised)', borderRadius: 'var(--radius-lg)' }}>
                No hay alertas ni eventos registrados para este conductor.
              </div>
            ) : (
              driverAlerts.map(alert => (
                <AlertCard key={alert.id} alert={alert} />
              ))
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
